import type { SvgIconComponent } from '@mui/icons-material'
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ForkRightOutlinedIcon from '@mui/icons-material/ForkRightOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import WebhookOutlinedIcon from '@mui/icons-material/WebhookOutlined'
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined'
import AlarmOutlinedIcon from '@mui/icons-material/AlarmOutlined'
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import ThumbsUpDownOutlinedIcon from '@mui/icons-material/ThumbsUpDownOutlined'
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined'

export interface TriggerDef {
  key: string
  label: string
  description: string
  icon: SvgIconComponent
  color: string
  category: string
}

export interface StepDef {
  key: string
  label: string
  description: string
  icon: SvgIconComponent
  color: string
}

export const TRIGGER_TYPES: TriggerDef[] = [
  { key: 'work_order.created', label: 'Work order created', description: 'When a new work order is created', icon: BuildOutlinedIcon, color: '#3B82F6', category: 'Work Orders' },
  { key: 'work_order.closed', label: 'Work order closed', description: 'When a work order is marked as done', icon: CheckCircleOutlinedIcon, color: '#059669', category: 'Work Orders' },
  { key: 'work_order.status_changed', label: 'WO status changed', description: 'When a work order changes status', icon: EditOutlinedIcon, color: '#8B5CF6', category: 'Work Orders' },
  { key: 'request.submitted', label: 'Request submitted', description: 'When a maintenance request is submitted', icon: AssignmentOutlinedIcon, color: '#D97706', category: 'Requests' },
  { key: 'asset.status_changed', label: 'Asset status changed', description: 'When an asset status changes', icon: CategoryOutlinedIcon, color: '#0E7490', category: 'Assets' },
  { key: 'part.low_stock', label: 'Inventory low stock', description: 'When a part drops below minimum quantity', icon: InventoryOutlinedIcon, color: '#DC2626', category: 'Inventory' },
  { key: 'asset.warranty_expiring', label: 'Warranty expiring', description: 'When an asset warranty is about to expire', icon: WarningAmberOutlinedIcon, color: '#EA580C', category: 'Assets' },
  { key: 'checklist.completed', label: 'Checklist completed', description: 'When a checklist is submitted on a work order', icon: ChecklistOutlinedIcon, color: '#059669', category: 'Checklists' },
  { key: 'asset.soft_point_reading', label: 'Soft point reading', description: 'When a reading lands on a specific asset telemetry channel', icon: SensorsOutlinedIcon, color: '#0EA5E9', category: 'Assets' },
  { key: 'schedule', label: 'Scheduled', description: 'Run on a recurring schedule', icon: RepeatOutlinedIcon, color: '#7C3AED', category: 'Time' },
  { key: 'manual', label: 'Manual trigger', description: 'Run manually from a button', icon: PlayArrowOutlinedIcon, color: '#64748B', category: 'Other' },
]

export const STEP_TYPES: StepDef[] = [
  { key: 'approval', label: 'Approval', description: 'Request approval from a person', icon: CheckCircleOutlinedIcon, color: '#059669' },
  { key: 'notification', label: 'Send notification', description: 'Notify a person or team', icon: NotificationsOutlinedIcon, color: '#3B82F6' },
  { key: 'email', label: 'Send email', description: 'Send an email to anyone', icon: EmailOutlinedIcon, color: '#0E7490' },
  { key: 'assign', label: 'Assign', description: 'Assign to a person or team', icon: PersonAddAltOutlinedIcon, color: '#8B5CF6' },
  { key: 'update_field', label: 'Update field', description: 'Change a field value', icon: EditOutlinedIcon, color: '#D97706' },
  { key: 'create_work_order', label: 'Create work order', description: 'Auto-create a new work order', icon: BuildOutlinedIcon, color: '#EA580C' },
  { key: 'create_task', label: 'Create task', description: 'Auto-create a soft-services task (no asset / vendor)', icon: AssignmentTurnedInOutlinedIcon, color: '#1D4ED8' },
  { key: 'attach_checklist', label: 'Attach checklist', description: 'Attach a checklist template to the work order', icon: ChecklistOutlinedIcon, color: '#059669' },
  { key: 'copy_request_attachments', label: 'Copy request attachments', description: 'Copy photos from the source request to the work order', icon: AttachFileOutlinedIcon, color: '#7C3AED' },
  { key: 'update_request_status', label: 'Approve / decline request', description: 'Auto-approve or decline the source request', icon: ThumbsUpDownOutlinedIcon, color: '#059669' },
  { key: 'webhook', label: 'Webhook', description: 'Send an HTTP request to an external URL', icon: WebhookOutlinedIcon, color: '#0891B2' },
  { key: 'condition', label: 'Condition', description: 'Branch based on a condition', icon: ForkRightOutlinedIcon, color: '#7C3AED' },
  { key: 'if_else', label: 'If / Else', description: 'Take different paths based on a condition', icon: AltRouteOutlinedIcon, color: '#7C3AED' },
  { key: 'switch', label: 'Switch (route by value)', description: 'Route to different steps based on a field value', icon: CallSplitOutlinedIcon, color: '#7C3AED' },
  { key: 'delay', label: 'Wait / Delay', description: 'Wait for a period of time', icon: ScheduleOutlinedIcon, color: '#64748B' },
  { key: 'escalation_timer', label: 'Escalation timer', description: 'Wait then escalate if condition is still unmet', icon: AlarmOutlinedIcon, color: '#DC2626' },
  { key: 'repeat_until', label: 'Repeat until', description: 'Repeat notifications until a condition is met', icon: LoopOutlinedIcon, color: '#D97706' },
]

export const TRIGGER_MAP = Object.fromEntries(TRIGGER_TYPES.map(t => [t.key, t]))
export const STEP_MAP = Object.fromEntries(STEP_TYPES.map(s => [s.key, s]))

export interface TriggerConditionDef {
  key: string
  label: string
  // 'site' / 'location' render entity pickers backed by the API.
  // 'time_window' renders start/end HH:MM inputs + a timezone dropdown.
  // 'asset_soft_point' renders an asset picker + a soft-point picker
  // scoped to it, and writes BOTH an `asset_id` and a `soft_point_id`
  // key (unlike every other type here, which writes just `c.key`).
  // The engine treats missing values as "no filter" — same semantics as
  // the existing `select` type with an empty value.
  type: 'select' | 'text' | 'checklist' | 'site' | 'location' | 'time_window' | 'asset_soft_point'
  options?: Array<{ value: string; label: string }>
  helper?: string
}

export const TRIGGER_CONDITIONS: Record<string, TriggerConditionDef[]> = {
  'work_order.created': [
    { key: 'priority', label: 'Only when priority is', type: 'select', options: [{ value: '', label: 'Any' }, { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' }] },
    { key: 'work_type', label: 'Only when work type is', type: 'select', options: [{ value: '', label: 'Any' }, { value: 'reactive', label: 'Reactive' }, { value: 'preventive', label: 'Preventive' }, { value: 'inspection', label: 'Inspection' }, { value: 'safety', label: 'Safety' }] },
    // Site / location filters. Leave them blank ("Any") to keep the
    // existing priority / work-type filters as the sole matchers.
    { key: 'site_id',     label: 'Only for site',     type: 'site' },
    { key: 'location_id', label: 'Only for location', type: 'location', helper: 'Pick a site first to narrow the list.' },
  ],
  'work_order.closed': [
    { key: 'priority', label: 'Only when priority is', type: 'select', options: [{ value: '', label: 'Any' }, { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' }] },
    { key: 'site_id',     label: 'Only for site',     type: 'site' },
    { key: 'location_id', label: 'Only for location', type: 'location', helper: 'Pick a site first to narrow the list.' },
  ],
  'work_order.status_changed': [
    { key: 'status', label: 'Only when new status is', type: 'select', options: [{ value: '', label: 'Any' }, { value: 'in_progress', label: 'In Progress' }, { value: 'closed', label: 'Closed' }, { value: 'resolved', label: 'Resolved' }] },
    { key: 'priority', label: 'Only when priority is', type: 'select', options: [{ value: '', label: 'Any' }, { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' }] },
    { key: 'site_id',     label: 'Only for site',     type: 'site' },
    { key: 'location_id', label: 'Only for location', type: 'location', helper: 'Pick a site first to narrow the list.' },
  ],
  'request.submitted': [
    { key: 'priority', label: 'Only when priority is', type: 'select', options: [{ value: '', label: 'Any' }, { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' }] },
    { key: 'site_id',     label: 'Only from site',     type: 'site' },
    { key: 'location_id', label: 'Only from location', type: 'location', helper: 'Pick a site first to narrow the list.' },
    { key: 'source', label: 'Only when source is', type: 'select', options: [
      { value: '', label: 'Any' },
      { value: 'internal',           label: 'Submitted inside the app' },
      { value: 'public_qr',          label: 'Public QR scan' },
      { value: 'email',              label: 'Inbound email' },
      { value: 'arcos_alarm',        label: 'IoT alarm (Arcos)' },
      { value: 'checklist_follow_up', label: 'Checklist follow-up' },
    ] },
    { key: 'time_window', label: 'Only during time window', type: 'time_window', helper: 'Fires only if the request comes in between these two times (24-hour). Timezone is IANA, e.g. Asia/Kolkata or UTC — leave blank for UTC. Wrap-around windows work: 22:00 → 06:00 covers after-hours.' },
  ],
  'asset.status_changed': [
    { key: 'status', label: 'Only when new status is', type: 'select', options: [{ value: '', label: 'Any' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'under_maintenance', label: 'Under Maintenance' }, { value: 'decommissioned', label: 'Decommissioned' }] },
  ],
  'asset.warranty_expiring': [
    { key: 'min_days', label: 'How many days before expiry?', type: 'select', options: [{ value: '7', label: '7 days before' }, { value: '14', label: '14 days before' }, { value: '30', label: '30 days before' }, { value: '60', label: '60 days before' }, { value: '90', label: '90 days before' }] },
  ],
  'checklist.completed': [
    { key: 'template_id', label: 'Only for checklist', type: 'checklist' },
  ],
  'asset.soft_point_reading': [
    { key: 'asset_soft_point', label: 'Watch this soft point', type: 'asset_soft_point',
      helper: 'Pick the exact asset and soft point to watch. Add a step-level "Condition" below to check the reading\'s value (equals, greater/less than, or "occurred N times in the last M readings").' },
  ],
  'part.low_stock': [],
  'schedule': [
    { key: 'frequency', label: 'How often?', type: 'select', options: [
      { value: 'every_hour', label: 'Every hour' },
      { value: 'every_day', label: 'Every day' },
      { value: 'every_week', label: 'Every week' },
      { value: 'every_month', label: 'Every month' },
      { value: 'every_quarter', label: 'Every 3 months' },
      { value: 'every_year', label: 'Every year' },
      { value: 'custom', label: 'Custom schedule...' },
    ]},
  ],
  'manual': [],
}

export interface TriggerExample {
  description: string
  useCase: string
  exampleFlow: Array<{ type: string; label: string }>
}

export const TRIGGER_EXAMPLES: Record<string, TriggerExample> = {
  'work_order.created': {
    description: 'Fires every time a new work order is created. Use conditions to target specific priorities or work types.',
    useCase: 'Auto-assign critical jobs and alert management instantly.',
    exampleFlow: [
      { type: 'condition', label: 'If priority = critical' },
      { type: 'assign', label: 'Assign to Senior Tech' },
      { type: 'notification', label: 'Notify Manager' },
      { type: 'email', label: 'Alert Safety Team' },
    ],
  },
  'work_order.closed': {
    description: 'Fires when a technician marks a work order as done. Use this for post-completion approvals and reports.',
    useCase: 'Multi-level sign-off before a job is fully resolved.',
    exampleFlow: [
      { type: 'approval', label: 'Supervisor Approval' },
      { type: 'approval', label: 'Manager Approval' },
      { type: 'email', label: 'Send Completion Report' },
    ],
  },
  'work_order.status_changed': {
    description: 'Fires when a work order moves to a new status. You can filter to specific statuses.',
    useCase: 'Keep the reporter informed as work progresses.',
    exampleFlow: [
      { type: 'notification', label: 'Notify Reporter' },
      { type: 'email', label: 'Status Update Email' },
    ],
  },
  'request.submitted': {
    description: 'Fires when anyone submits a maintenance request. Perfect for triage workflows.',
    useCase: 'Auto-triage incoming requests to the right team.',
    exampleFlow: [
      { type: 'notification', label: 'Notify Maintenance Mgr' },
      { type: 'condition', label: 'If priority = high' },
      { type: 'create_work_order', label: 'Auto-create WO' },
    ],
  },
  'asset.status_changed': {
    description: 'Fires when an asset status changes (e.g. active → under maintenance). Use for reactive workflows.',
    useCase: 'Auto-create inspection WOs when assets go offline.',
    exampleFlow: [
      { type: 'condition', label: 'If status = inactive' },
      { type: 'create_work_order', label: 'Create Inspection WO' },
      { type: 'notification', label: 'Alert Asset Manager' },
    ],
  },
  'asset.warranty_expiring': {
    description: 'Fires when an asset warranty is about to expire. Set how many days before to trigger.',
    useCase: 'Never miss a warranty renewal again.',
    exampleFlow: [
      { type: 'email', label: 'Email Procurement' },
      { type: 'notification', label: 'Notify Asset Manager' },
      { type: 'create_work_order', label: 'Create Renewal Task' },
    ],
  },
  'checklist.completed': {
    description: 'Fires when a checklist response is submitted on a work order. Use to trigger follow-up actions after inspections.',
    useCase: 'Auto-notify managers and create follow-up WOs after inspections.',
    exampleFlow: [
      { type: 'condition', label: 'If score < 70%' },
      { type: 'create_work_order', label: 'Create Corrective WO' },
      { type: 'notification', label: 'Notify Safety Officer' },
    ],
  },
  'part.low_stock': {
    description: 'Fires when any part quantity drops below its minimum stock level.',
    useCase: 'Automatic reorder and inventory alerts.',
    exampleFlow: [
      { type: 'email', label: 'Email Vendor to Reorder' },
      { type: 'notification', label: 'Notify Inventory Mgr' },
      { type: 'create_work_order', label: 'Track Reorder' },
    ],
  },
  'asset.soft_point_reading': {
    description: 'Fires every time a reading lands on the specific asset + soft point you pick — from a checklist answer, a linked device, or a manual/CSV entry. Add a step-level Condition to check the value.',
    useCase: 'Auto-create a work order when a feedback rating hits "Very Poor", or repeats too many times.',
    exampleFlow: [
      { type: 'condition', label: 'If value = 1' },
      { type: 'create_work_order', label: 'Create Follow-up WO' },
      { type: 'notification', label: 'Notify Site Manager' },
    ],
  },
  'schedule': {
    description: 'Runs on a recurring schedule — daily, weekly, or monthly. Great for routine inspections.',
    useCase: 'Automate recurring preventive maintenance tasks.',
    exampleFlow: [
      { type: 'create_work_order', label: 'Create PM Work Order' },
      { type: 'assign', label: 'Assign to Duty Tech' },
      { type: 'notification', label: 'Notify Team' },
    ],
  },
  'manual': {
    description: 'Only runs when you click "Run now". Use for on-demand actions that don\'t have a natural trigger.',
    useCase: 'One-click emergency protocols or batch operations.',
    exampleFlow: [
      { type: 'notification', label: 'Alert All Technicians' },
      { type: 'create_work_order', label: 'Create Emergency WO' },
      { type: 'email', label: 'Notify Management' },
    ],
  },
}

// Keep backward compat
export const TRIGGER_HELP: Record<string, string> = Object.fromEntries(
  Object.entries(TRIGGER_EXAMPLES).map(([k, v]) => [k, `Example: ${v.useCase}`])
)

// Help text for each step type
export const STEP_HELP: Record<string, string> = {
  'approval': 'Pauses the workflow until the selected person approves or rejects. Use for sign-offs.',
  'notification': 'Sends an in-app notification + email to a specific person.',
  'email': 'Sends an email to any address — vendors, external contacts, etc.',
  'assign': 'Changes who is assigned to the work order.',
  'update_field': 'Automatically updates a field like priority, status, or category.',
  'create_work_order': 'Creates a new work order automatically with pre-set values.',
  'attach_checklist': 'Attaches a checklist template to the work order so the technician must complete it.',
  'webhook': 'Sends an HTTP request to an external URL — integrate with Slack, Teams, PagerDuty, Zapier, or any API.',
  'condition': 'Checks a condition — if false, remaining steps are skipped.',
  'if_else': 'Evaluates a condition and takes one of two paths. Use for "if critical → do X, else → do Y" logic.',
  'switch': 'Routes to a set of steps based on a field value. Cleaner than nested if/else when you have more than two branches (e.g. route by location A / B / C).',
  'create_task': 'Same as Create work order but the row is tagged as a task (kind=task, TSK-XXXXX code). Tasks are soft-services / internal work — no asset or vendor. Use for cleaning, restocking, admin work.',
  'copy_request_attachments': 'Copies every photo attached to the source request onto the work order. Requires the trigger to be a request event.',
  'update_request_status': 'Marks the source request as approved or declined. Fires the same emails as the manual buttons.',
  'delay': 'Waits for a set time before continuing to the next step.',
  'escalation_timer': 'Waits a set time, then checks if a condition is still unmet. If so, runs escalation actions (e.g. reassign, notify manager).',
  'repeat_until': 'Repeats a set of actions (e.g. send reminder) on an interval until a stop condition is met (e.g. WO is closed).',
}
